<template>
  <v-stepper v-model="stepTracker" class="primary">
    <v-stepper-header>
      <v-stepper-step :complete="stepTracker > 1" step="1">Primary</v-stepper-step>
      <v-divider />
      <v-stepper-step :complete="stepTracker > 2" step="2">Tertiary</v-stepper-step>
      <v-divider />
      <v-stepper-step :complete="stepTracker > 3" step="3">Assign</v-stepper-step>
      <v-divider />
      <v-stepper-step :complete="stepTracker > 4" step="4">Races</v-stepper-step>
      <v-divider />
      <v-stepper-step :complete="stepTracker > 5" step="5">Traits</v-stepper-step>
      <v-divider />
      <v-stepper-step :complete="stepTracker > 6" step="6">Skills</v-stepper-step>
      <v-divider />
      <v-stepper-step :complete="stepTracker > 7" step="7">Finalize</v-stepper-step>
    </v-stepper-header>

    <v-stepper-items>
      <!-- Display the primary stat selection options and only let them continue if they have exactly two options selected -->
      <v-stepper-content step="1">
        <v-card color="secondary" elevation="0" class="white--text">
          <v-card-title>Pick Two Primary Stats</v-card-title>
          <v-card-text class="white--text">
            Your primary stats will grant you access to a variety of traits based off of them. Increasing your primary stats is cheaper than increasing secondary stats.
            <v-item-group v-model="character.primaryStats" multiple >
              <v-row>
                <v-col v-for="(item, i) in stats" :key="i" cols="12" md="6" xl="3">
                  <v-item :value="stats[i]" v-slot="{ active, toggle }">
                    <v-card color="primary" class="white--text" @click="toggle">
                      <v-card-title>
                        {{ item }}
                        <v-spacer />
                        <v-icon color="accent">
                          {{ active ? 'mdi-checkbox-marked-circle' : 'mdi-checkbox-blank-circle'  }}
                        </v-icon>
                      </v-card-title>
                      <v-card-text>
                        <v-sheet dark color="secondary">
                          <contentEngine
                            :contentTitle="item"
                            headerType="None"
                            leftAlign
                            :editMode="false"
                            :contentTagging="contentTagging"
                            :sections="sections"
                            :examples="{}" />
                          <br>
                        </v-sheet>
                      </v-card-text>
                    </v-card>
                  </v-item>
                </v-col>
              </v-row>
            </v-item-group>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn color="general" @click="character.tertiaryStats = []; stepTracker++" :disabled="character.primaryStats.length !== 2">Continue</v-btn>
            <!-- When progressing to the next step reset the tertiary stat options that they may have previously chosen -->
          </v-card-actions>
        </v-card>
      </v-stepper-content>

      <!-- Display the tertiary stat selection options and only let them continue if they have exactly one options selected -->
      <v-stepper-content step="2">
        <v-card color="secondary" elevation="0" class="white--text">
          <v-card-title>Pick One Tertiary Stat</v-card-title>
          <v-card-text class="white--text">
            Your tertiary stat further grants your character access to traits but in a more limited capacity. Increasing your tertiary stat is more expensive than your primary stats.
            <v-item-group v-model="character.tertiaryStats" multiple >
              <v-row>
                <template v-for="(item, i) in stats">
                  <!-- Don't display stats they chose as primary -->
                  <v-col v-if="!character.primaryStats.includes(stats[i])" :key="i" cols="12" md="6" xl="3">
                    <v-item :value="stats[i]" v-slot="{ active, toggle }">
                      <v-card color="primary" class="white--text" @click="toggle">
                        <v-card-title>
                          {{ item }}
                          <v-spacer />
                          <v-icon color="accent">
                            {{ active ? 'mdi-checkbox-marked-circle' : 'mdi-checkbox-blank-circle'  }}
                          </v-icon>
                        </v-card-title>
                        <v-card-text class="white--text">
                          <v-sheet dark color="secondary">
                            <contentEngine
                              :contentTitle="item"
                              headerType="None"
                              leftAlign
                              :editMode="false"
                              :contentTagging="contentTagging"
                              :sections="sections"
                              :examples="{}" />
                            <br>
                          </v-sheet>
                        </v-card-text>
                      </v-card>
                    </v-item>
                  </v-col>
                </template>
              </v-row>
            </v-item-group>
          </v-card-text>
          <v-card-actions>
            <v-btn color="general" @click="stepTracker--">Back</v-btn>
            <v-spacer />
            <v-btn color="general" @click="setStats(); stepTracker++" :disabled="character.tertiaryStats.length !== 1">Continue</v-btn>
            <!-- When they proceed reset the stat points they have to spend as well as their character attributes -->
          </v-card-actions>
        </v-card>
      </v-stepper-content>

      <!-- Load the characterStats component and only allow them to continue once all points have been spent -->
      <v-stepper-content step="3">
        <v-card color="secondary" elevation="0" class="white--text">
          <v-card-title>Assign Stat Points</v-card-title>
          <v-card-text class="white--text">
            <characterStats v-if="stepTracker === 3" :character="character" edit />
          </v-card-text>
          <v-card-actions>
            <v-btn color="general" @click="stepTracker--">Back</v-btn>
            <v-spacer />
            <v-btn color="general" @click="character.resetTraits = []; stepTracker++" :disabled="character.statPoints > 0">Continue</v-btn>
            <!-- resetTraits is the users trait selections that have not been finalized yet - reset those selections when continuing -->
          </v-card-actions>
        </v-card>
      </v-stepper-content>
      
      <!-- Provide the user with a list of races and only allow them to continue once they have one selected -->
      <v-stepper-content step="4">
        <v-card color="secondary" elevation="0" class="white--text">
          <v-card-title>Choose your Race</v-card-title>
          <v-card-text>
            <v-sheet color="primary" class="white--text">
              <v-row justify="center">
                <v-col cols="12" sm="4">
                  <v-list-item-group v-model="character.race" dark @change="raceKey++">
                    <v-list-item v-for="(race, i) in raceList" :value="race" :key="i">
                      <v-list-item-content>
                        <v-list-item-title v-text="race.name" />
                      </v-list-item-content>
                    </v-list-item>
                  </v-list-item-group>
                </v-col>
                <v-col cols="12" sm="8">
                  <v-sheet color="secondary" class="white--text mr-3 pa-2" height="100%">
                    <template v-if="character.race">
                      {{ character.race.hp }} racial HP per level
                      <br>
                      {{ character.race.skillPoints }} racial Skill Points per level
                      <br>
                      {{ character.race.other }}
                      <br>
                      <contentEngine
                        :contentTitle="character.race.name"
                        headerType="None"
                        leftAlign
                        :editMode="false"
                        :contentTagging="contentTagging"
                        :sections="sections"
                        :examples="{}"
                        :key="raceKey" />
                    </template>
                  </v-sheet>
                </v-col>
              </v-row>
            </v-sheet>
          </v-card-text>
          <v-card-actions>
            <v-btn color="general" @click="setStats(); stepTracker--">Back</v-btn>
            <!-- If they go back reset the stats to prevent a misalignment with spent skill points -->
            <v-spacer />
            <v-btn color="general" @click="character.traits = [];stepTracker++" :disabled="!character.race">Continue</v-btn>
            <!-- If they go forward reset their selectedTraits -->
          </v-card-actions>
        </v-card>
      </v-stepper-content>

      <!-- Display the trait selector and only allow them to continue once they've chosen two traits -->
      <v-stepper-content step="5">
        <v-card color="secondary" elevation="0" class="white--text">
          <v-card-title>Choose Two Traits</v-card-title>
          <v-card-text class="white--text">
            <characterTraits :character="character" />
          </v-card-text>
          <v-card-actions>
            <v-btn color="general" @click="stepTracker--">Back</v-btn>
            <v-spacer />
            <v-btn color="general" @click="setTraits(); calculateSkills(); stepTracker++" :disabled="character.resetTraits.length !== 2">Continue</v-btn>
            <!-- When they continue 'lock in' their chosen traits and calculate their skill points -->
          </v-card-actions>
        </v-card>
      </v-stepper-content>

      <!-- Display the skill points component and only let them continue once they've spent all their skill points -->
      <v-stepper-content step="6">
        <v-card color="secondary" elevation="0" class="white--text">
          <v-card-title>Spend Skill Points</v-card-title>
          <v-card-text class="white--text">
            <characterSkills :character="character"/>
          </v-card-text>
          <v-card-actions>
            <v-btn color="general" @click="resetSelections(); stepTracker--">Back</v-btn>
            <v-spacer />
            <v-btn color="general" @click="character.traitPoints = 0; character.hpRolls = []; stepTracker++" :disabled="skillsContinue">Continue</v-btn>
            <!-- Lock in their traits as 'spent' when they go forward. This retains their chosen traits but keeps them from getting more for 'free' -->
          </v-card-actions>
        </v-card>
      </v-stepper-content>

      <!-- Show the characters details and only let them complete character creation once the character is named -->
      <v-stepper-content step="7">
        <v-card color="secondary" elevation="0" class="white--text">
          <v-card-title>Finalize</v-card-title>
          <v-card-text>
            <characterView v-if="stepTracker === 7" :character="character" chosenName="" />
          </v-card-text>
          <v-card-actions>
            <v-btn color="general" @click="character.traitPoints = 2; stepTracker--">Back</v-btn>
            <!-- When they go back give them two trait points in case they go back far enough to spend them -->
            <v-spacer />
            <v-btn color="accent" @click="saveCharacter()" :disabled="character.name === '' || existing.includes(character.name)">Create</v-btn>
          </v-card-actions>
        </v-card>
      </v-stepper-content>
    </v-stepper-items>
  </v-stepper>
</template>

<script>
import _ from 'lodash';
import { mapState, mapGetters } from 'vuex';

export default {
  components: {
    contentEngine: () => import('../../contentEngine/contentEngine'),
    characterStats: () => import('./characterStats'),
    characterTraits: () => import('./characterTraits'),
    characterSkills: () => import('./characterSkills'),
    characterView: () => import('./characterView.vue')
  },
  props: {
    character: {
      type: Object,
      required: true
    },
    existing: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      stepTracker: 1,
      raceKey: 0,
      stats: ['Physique', 'Finesse', 'Mind', 'Spirit']
    };
  },
  methods: {
    setStats() {
      this.character.statPoints = 6;
      this.stats.forEach((stat) => {
        if (this.character.primaryStats.includes(stat) || this.character.tertiaryStats.includes(stat)) {
          this.character[stat.toLowerCase()] = 4;
        } else {
          this.character.dumpStats = [stat];
          this.character[stat.toLowerCase()] = 3;
        }
      });
    },
    setTraits() {
      this.character.traits = _.cloneDeep(this.character.resetTraits);
    },
    resetSelections() {
      // When going 'back' in to the traits step we need to clear out the confirmed traits but keep the trait selections (resetTraits) chosen
      const localSelections = _.cloneDeep(this.character.traits);
      this.character.traits = [];
      this.character.resetTraits = _.cloneDeep(localSelections);
    },
    calculateSkills() {
      this.character.resetSkills.skillPoints.physique = this.character.physique;
      this.character.resetSkills.skillPoints.finesse = this.character.finesse;
      this.character.resetSkills.skillPoints.mind = this.character.mind;
      this.character.resetSkills.skillPoints.spirit = this.character.spirit;
      this.character.resetSkills.skillPoints.specialty = Math.ceil((this.character.mind - 3) / 3);
      const racialModifier = parseInt(this.character.race.skillPoints, 10);
      this.character.resetSkills.skillPoints.general = Math.ceil(this.character.mind / 3) + 3 + racialModifier; // '+ 3' = 1 from normal equation +2 from character creation
      this.character.skills = _.cloneDeep(this.character.resetSkills);
    },
    saveCharacter() {
      this.character.resetSkills = _.cloneDeep(this.character.skills);
      this.$emit('create', this.character);
    }
  },
  computed: {
    ...mapState(['contentTagging']),
    ...mapState('actsContent', ['sections']),
    skillsContinue() {
      const sum = this.character.skills.skillPoints.physique
        + this.character.skills.skillPoints.finesse
        + this.character.skills.skillPoints.mind
        + this.character.skills.skillPoints.spirit
        + this.character.skills.skillPoints.specialty
        + this.character.skills.skillPoints.general;
      if (sum > 0) {
          return true;
      }
      return false;
    },
    raceList() {
      return this.sections['table|3-1'].items;
    } 

  }
};
</script>

<style></style>
